import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Pencil, ShieldCheck, ShieldOff, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  PENDING_VERIFICATION: "bg-slate-500/10 text-slate-300 border-slate-500/20",
  ACTIVE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  GRACE: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  SUSPENDED: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editDisabled, setEditDisabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [editPlanId, setEditPlanId] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [verification, setVerification] = useState("all");
  const [payment, setPayment] = useState("all");
  const [usage, setUsage] = useState("all");
  const [summary, setSummary] = useState({ all: 0, verified: 0, unverified: 0, paid: 0, profiles: 0 });
  const limit = 20;

  const loadUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        search,
        page: String(page),
        limit: String(limit),
        verification,
        payment,
        usage,
      });
      const res = await api.get(`/admin/users?${params.toString()}`);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setSummary(res.data.summary || {});
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [search, page, verification, payment, usage]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => {
    api.get("/admin/plans")
      .then((res) => setPlans(Array.isArray(res.data) ? res.data : (res.data?.plans || [])))
      .catch(() => toast.error("Failed to load plans"));
  }, []);

  const resolvePlanId = useCallback((user, planList) => {
    if (!user) return "";
    if (user.subscription_plan_id && planList.some((p) => p.id === user.subscription_plan_id)) {
      return user.subscription_plan_id;
    }
    const byName = planList.find(
      (p) => (p.name || "").toLowerCase() === String(user.subscription_plan || "").toLowerCase()
    );
    if (byName) return byName.id;
    const bySlug = planList.find(
      (p) => (p.slug || "").toLowerCase() === String(user.subscription_plan || "").toLowerCase().replace(/\s+/g, "-")
    );
    if (bySlug) return bySlug.id;
    return planList.find((p) => p.slug === "free")?.id || "";
  }, []);

  useEffect(() => {
    if (!editUser || !plans.length) return;
    setEditPlanId((current) => current || resolvePlanId(editUser, plans));
  }, [editUser, plans, resolvePlanId]);

  const openEdit = (user) => {
    setEditUser(user);
    setEditStatus(String(user.subscription_status || "ACTIVE").toUpperCase());
    setEditRole(String(user.role || "USER").toUpperCase());
    setEditDisabled(Boolean(user.disabled));
    setEditPlanId(resolvePlanId(user, plans));
  };

  const apiErrorMessage = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length) {
      return detail
        .map((item) => (typeof item === "string" ? item : item?.msg || JSON.stringify(item)))
        .join("; ");
    }
    if (detail && typeof detail === "object" && detail.msg) return detail.msg;
    return fallback;
  };

  const handleSave = async () => {
    if (!editUser?.id) return;
    if (!editPlanId) {
      toast.error("Select a product plan before saving");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/users/${editUser.id}`, {
        subscription_status: String(editStatus || "ACTIVE").toUpperCase(),
        role: String(editRole || "USER").toUpperCase(),
        disabled: Boolean(editDisabled),
        subscription_plan_id: editPlanId,
      });
      setEditUser(null);
      toast.success("User updated");
      loadUsers();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to update user"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      setEditUser(null);
      toast.success("User deleted successfully");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">User Management</h1>
        <p className="text-muted-foreground text-sm">{total} total users</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="customer-summary">
        {[
          ["Accounts", summary.all],
          ["Verified", summary.verified],
          ["Unverified", summary.unverified],
          ["Paid by Stripe", summary.paid],
          ["Using Smart QR", summary.profiles],
        ].map(([label, value]) => (
          <Card key={label}><CardContent className="p-4"><p className="text-xs text-muted-foreground">{label}</p><p className="text-xl font-bold mt-1">{value || 0}</p></CardContent></Card>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
            data-testid="admin-user-search"
          />
        </div>
        <Select value={verification} onValueChange={(value) => { setVerification(value); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="admin-verification-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All verification</SelectItem>
            <SelectItem value="verified">Verified only</SelectItem>
            <SelectItem value="unverified">Awaiting verification</SelectItem>
          </SelectContent>
        </Select>
        <Select value={payment} onValueChange={(value) => { setPayment(value); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="admin-payment-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All payments</SelectItem>
            <SelectItem value="paid">Stripe paid</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="manual">Admin assigned</SelectItem>
          </SelectContent>
        </Select>
        <Select value={usage} onValueChange={(value) => { setUsage(value); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="admin-usage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All usage</SelectItem>
            <SelectItem value="with_profile">Has profile</SelectItem>
            <SelectItem value="no_profile">No profile</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="admin-users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Profile</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No users found</TableCell></TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium text-sm">
                      {u.email}
                      {u.disabled && <Badge variant="destructive" className="ml-2 text-[10px]">Disabled</Badge>}
                    </TableCell>
                    <TableCell><Badge variant={u.email_verified ? "default" : "outline"} className="text-xs">{u.email_verified ? "Verified" : "Waiting"}</Badge></TableCell>
                    <TableCell>
                      <Badge variant={u.role === "ADMIN" ? "default" : "secondary"} className="text-xs">
                        {u.role || "USER"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${statusColors[u.subscription_status] || statusColors.ACTIVE}`}>
                        {u.subscription_status || "ACTIVE"}
                      </Badge>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{u.subscription_plan || "Free"}</Badge></TableCell>
                    <TableCell><Badge variant={u.payment_state === "paid" ? "default" : "outline"} className="text-xs capitalize">{u.payment_state || "free"}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {u.profile ? `@${u.profile.username}` : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(u.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`admin-edit-user-${u.id}`}>
                        <Pencil size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}

      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs text-muted-foreground">Email</Label>
                <p className="text-sm font-medium">{editUser.email}</p>
              </div>
              <div className="space-y-2">
                <Label>Subscription Status</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger data-testid="admin-edit-user-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING_VERIFICATION">PENDING VERIFICATION</SelectItem>
                    <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                    <SelectItem value="GRACE">GRACE</SelectItem>
                    <SelectItem value="SUSPENDED">SUSPENDED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Product Plan</Label>
                <Select value={editPlanId} onValueChange={setEditPlanId}>
                  <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
                  <SelectContent>
                    {plans.map((plan) => <SelectItem key={plan.id} value={plan.id}>{plan.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">Plan changes immediately update product access.</p>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger data-testid="admin-edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USER">USER</SelectItem>
                    <SelectItem value="ADMIN">ADMIN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label>Account Status</Label>
                <Button
                  variant={editDisabled ? "destructive" : "outline"}
                  size="sm"
                  onClick={() => setEditDisabled(!editDisabled)}
                  data-testid="admin-toggle-disabled"
                >
                  {editDisabled ? <><ShieldOff size={14} className="mr-1" /> Disabled</> : <><ShieldCheck size={14} className="mr-1" /> Enabled</>}
                </Button>
              </div>

              {/* Delete Section */}
              <div className="border-t border-destructive/20 pt-4 mt-4">
                <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-destructive shrink-0 mt-0.5" size={18} />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-destructive">Danger Zone</h4>
                      <p className="text-xs text-muted-foreground mt-1 mb-3">
                        Permanently delete this user and all their data (profile, links, QR codes, analytics). This action cannot be undone.
                      </p>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteConfirm(editUser)}
                        data-testid="admin-delete-user-btn"
                      >
                        <Trash2 size={14} className="mr-1.5" />
                        Delete User
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="admin-save-user-btn">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle size={20} />
              Confirm Delete
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to permanently delete the user <strong className="text-foreground">{deleteConfirm?.email}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 my-2">
            <p className="text-sm text-muted-foreground">
              This will delete:
            </p>
            <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
              <li>User account and login credentials</li>
              <li>Profile and all personal information</li>
              <li>All links and QR codes</li>
              <li>All analytics and scan history</li>
              <li>Payment and subscription records</li>
            </ul>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="admin-confirm-delete-btn">
              {deleting ? "Deleting..." : "Yes, Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
